import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FollowUpAutomationsPanel } from '../../admin/components/FollowUpAutomationsPanel';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes, PermissionScopes } from '../../auth/permissions';
import { CommentAutomationsPage } from '../../comment-automations';
import { DmAutomationsPage } from '../../dm-automations';

type AutomationsTab = 'comments' | 'dms' | 'quiet';

const automationsTabs: Array<{ id: AutomationsTab; labelKey: string }> = [
  { id: 'comments', labelKey: 'commentsTab' },
  { id: 'dms', labelKey: 'dmsTab' },
  { id: 'quiet', labelKey: 'quietChatsTab' },
];

export const AutomationsPage: React.FC = () => {
  const { t } = useTranslation(['automations']);
  const { hasPermission } = useAuth();
  const canReadQuietChats = hasPermission(PermissionScopes.aiConfigRead);
  const canManageQuietChats = hasPermission([PermissionCodes.aiConfigManage, PermissionCodes.knowledgeManage]);
  const [searchParams, setSearchParams] = useSearchParams();
  const visibleTabs = automationsTabs.filter((tab) => tab.id !== 'quiet' || canReadQuietChats);
  const requested = searchParams.get('tab');
  const activeTab: AutomationsTab = visibleTabs.some((tab) => tab.id === requested)
    ? (requested as AutomationsTab)
    : 'comments';

  const handleTabChange = (tab: AutomationsTab) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', tab);
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="company-admin-page">
      <div className="company-page-head">
        <h2>{t('title')}</h2>
        <div className="company-tabs" role="tablist" aria-label={t('managementTabs')}>
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`company-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >
              {t(tab.labelKey)}
            </button>
          ))}
        </div>
      </div>

      <div className="company-tab-panel">
        {activeTab === 'comments' ? <CommentAutomationsPage /> : null}
        {activeTab === 'dms' ? <DmAutomationsPage /> : null}
        {activeTab === 'quiet' ? <FollowUpAutomationsPanel canManage={canManageQuietChats} /> : null}
      </div>
    </div>
  );
};
